<template>
  <v-container fluid class="TwoFactor">
    <div class="TwoFactor-content">
      <v-row justify="center" class="mt-10 mb-10">
        <h2 class="text-text-primary">{{$t('login.views.TwoFactor.twoFactorTitle')}}</h2>
      </v-row>
      <v-row>
        <AppCard class="text-text-secondary">
          <v-row justify="center" class="mt-2 text-center">
            <v-col>
              {{ $t('login.views.TwoFactor.title') }}
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-center">
              <img
                width="205"
                src="@/assets/img/2FaIllustration.svg"
                alt="2FA Illustration"
              />
            </v-col>
          </v-row>
          <v-row justify="center" class="mt-4 mb-4 text-center">
              {{ $t('login.views.TwoFactor.explainText1') }}<br />
              {{ $t('login.views.TwoFactor.explainText2') }}
          </v-row>
          <v-row justify="center">
            <div class="d-flex align-center">
              <app-material-icon
                name="key"
                size="md"
                class="mr-4"
              ></app-material-icon>
              <v-otp-input v-model.trim="twoFactorCode"
                           length="6"
                           @keypress.enter="prepareTwoFactorSend"
              />
            </div>
            <div v-if="twoFactorErrors.length > 0" class="text-error">
              <div v-for="(error, index) in twoFactorErrors" :key="index">{{ error }}</div>
            </div>
          </v-row>
          <v-row justify="center" class="mt-4">
            <v-col class="px-8">
              <app-alert-in-page
                variant="warning"
                :dismissible="false"
                data-locator="close-window-warning"
              >
                {{ $t('login.views.TwoFactor.closeWindowWarning') }}
              </app-alert-in-page>
            </v-col>
          </v-row>
          <v-row justify="center" class="my-10">
            <app-button
              :loading="loading"
              @click="prepareTwoFactorSend"
            >
              {{$t('login.views.TwoFactor.sendCode')}}
            </app-button>
          </v-row>
          <v-row class="mt-5">
            <v-divider color="border-primary" />
          </v-row>
          <v-row v-if="!successResend" justify="center">
            <app-button
              class="TwoFactor-resend mt-10 mb-6"
              :disabled="!canResend"
              @click="prepareResend"
              >
                {{sendNewCodeLabel}}
              </app-button>
          </v-row>
          <v-row v-if="successResend" justify="center" align="center" class="mt-8 mb-1">
            <v-col cols="1">
              <app-material-icon
                name="check_circle"
                filled
                color="#2AAD3D"
                size="xl"
               />
            </v-col>
            <v-col cols="6">
              <p>{{$t('login.views.TwoFactor.successMsg')}}</p>
            </v-col>
          </v-row>
        </AppCard>
      </v-row>
      <v-row class="mt-3">
        <v-col class="d-flex justify-center TwoFactor-login">
          <router-link :to="{ name: LOGOUT_ROUTE_NAME }" class="text-text-tertiary body-md-bold">
            {{ $t("login.views.TwoFactor.backLogin") }}
          </router-link>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { ROOMS_ROUTE_NAME, LOGOUT_ROUTE_NAME } from '@/router/routerData'
import { RESEND_TWO_FACTORS_CODE, VALIDATE_TWO_FACTORS_CODE } from '@/store/modules/login/action_types'

export default {
  name: 'TwoFactor',
  components: { AppCard },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    twoFactorCode: { required },
  },
  props: {
    returnRoute: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      LOGOUT_ROUTE_NAME,
      twoFactorCode: '',
      successResend: false,
      countdown: 30,
    }
  },
  computed: {
    ...mapState('login', ['loading']),
    twoFactorErrors () {
      return formFieldErrors(
        this.v$.twoFactorCode,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.TwoFactor.inputCode') })],
        ],
      )
    },
    canResend () {
      return this.countdown === 0
    },
    sendNewCodeLabel () {
      let label = this.$t('login.views.TwoFactor.resendCode')
      if (!this.canResend) {
        label += ` (${this.countdown})`
      }
      return label
    },
  },
  watch: {
    countdown: {
      handler (value) {
        if (value > 0) {
          setTimeout(() => {
            this.countdown--
          }, 1000)
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('login', [RESEND_TWO_FACTORS_CODE, VALIDATE_TWO_FACTORS_CODE]),
    async prepareTwoFactorSend () {
      if (this.loading) {
        return
      }
      this.v$.twoFactorCode.$touch()
      if (!this.v$.twoFactorCode.$invalid) {
        try {
          const validate = await this.VALIDATE_TWO_FACTORS_CODE({
            code: this.twoFactorCode,
          })
          if (validate.success === true) {
            if (this.returnRoute !== '') {
              this.$router.push(this.returnRoute)
            } else {
              this.$router.push({ name: ROOMS_ROUTE_NAME })
            }
          } else {
            toast.error(this.$t('login.views.TwoFactor.codeError'))
          }
        } catch (e) {
          console.error(e)
          toast.error(this.$t('login.views.TwoFactor.submitError'))
        }
      }
    },
    async prepareResend () {
      try {
        await this.RESEND_TWO_FACTORS_CODE()
        this.successResend = true
        setTimeout(() => { this.successResend = false }, 5000)
        this.countdown = 30
      } catch (e) {
        console.error(e)
        toast.error(this.$t('login.views.TwoFactor.resendError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.TwoFactor-login a {
    text-decoration: none;
}
.TwoFactor {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: none;
  width: 100%;
}
.TwoFactor-content {
  box-sizing: border-box;
  max-width: 500px;
  padding: 12px;
  width: 100%;
}

input {
  border: none;
}

.v-field {
  --outline-color: rgb(var(--v-theme-border-secondary));
  --border-width: #{get-width('border-width-1')};

  .v-field__outline__start, .v-field__outline__end {
    border-top: var(--border-width) solid var(--outline-color);
    border-bottom: var(--border-width) solid var(--outline-color);
    opacity: 1;
  }

  .v-field__outline__start {
    border-left: var(--border-width) solid var(--outline-color);
  }

  .v-field__outline__end {
    border-right: var(--border-width) solid var(--outline-color);
  }

  &:hover {
    --outline-color: rgb(var(--v-theme-border-secondary-hover));
  }

  &:focus-within {
    --outline-color: rgb(var(--v-theme-border-info-bold));
    --border-width: #{get-width('border-width-2')};
  }
}
</style>
